// Pure logic for the two-click calendar date-range picker.
//
// Booking.com semantics: the first click in an open calendar anchors the
// start and clears the end; the second click on a valid day completes the
// range. A click before the anchor (or too close to honour minSpan)
// re-anchors instead of completing. All branching lives here so
// DateRangePicker stays a thin shell and the logic is node-testable without
// jsdom — mirroring the exported pure transitions in `useDateRange.ts`.

import { addMonthsToKey } from './months';
import { addDays, daysBetween } from './useDateRange';

/** Internal selection state while the calendar is open. */
export type SelectionState = {
  from: string;
  to: string;
  /** Start of an in-progress selection (first click). */
  anchor: string;
  /** True after the first click, before the second completes the range. */
  selecting: boolean;
};

export type ClickOptions = {
  minSpanDays: number;
  min?: string;
  max?: string;
};

export type ClickResult = {
  next: SelectionState;
  /** Present only when a click completes a valid range. */
  emit?: { from: string; to: string };
};

/** A day is selectable when it lies within the inclusive [min, max] bounds. */
export function isDayDisabled(day: string, opts: { min?: string; max?: string }): boolean {
  if (opts.min && day < opts.min) return true;
  if (opts.max && day > opts.max) return true;
  return false;
}

/**
 * Advance the two-click machine for a click on `day`.
 *
 *   1. Out of [min, max] → no-op (the cell is also greyed in the UI).
 *   2. Not selecting (fresh, or both ends already set) → anchor `day`, clear
 *      the end, start selecting. No emit.
 *   3. Selecting, `day >= anchor + minSpan` → complete: from = anchor,
 *      to = day. Emit. (minSpan 0 lets `day === anchor` complete a single-day
 *      range; minSpan ≥ 1 forbids a zero-length range.)
 *   4. Selecting, `day` before `anchor + minSpan` → re-anchor on `day`. No emit.
 */
export function applyDayClick(
  state: SelectionState,
  day: string,
  opts: ClickOptions,
): ClickResult {
  if (isDayDisabled(day, opts)) return { next: state };

  const minSpan = opts.minSpanDays;

  if (!state.selecting) {
    return { next: { from: '', to: '', anchor: day, selecting: true } };
  }

  // Second click completes only when far enough past the anchor.
  if (daysBetween(state.anchor, day) >= minSpan) {
    const range = { from: state.anchor, to: day };
    return {
      next: { ...range, anchor: '', selecting: false },
      emit: range,
    };
  }

  // Too early / too close — treat as a fresh first click.
  return { next: { from: '', to: '', anchor: day, selecting: true } };
}

export type DayRole = 'start' | 'end' | 'in-range' | 'tentative' | 'none';

/**
 * Classify a day for cell styling. While selecting (only `anchor` set, no
 * committed `to`), the band between the anchor and the hovered day renders as
 * `tentative`; once a range is committed, days render as start / end / in-range.
 */
export function dayRangeRole(
  day: string,
  state: { from: string; to: string; anchor: string; selecting: boolean; hovered?: string },
): DayRole {
  // Mid-selection: highlight the anchor plus the tentative band to the hover.
  if (state.selecting && state.anchor) {
    if (day === state.anchor) return 'start';
    if (state.hovered) {
      const [lo, hi] = state.anchor <= state.hovered
        ? [state.anchor, state.hovered]
        : [state.hovered, state.anchor];
      if (day === state.hovered) return 'end';
      if (day > lo && day < hi) return 'tentative';
    }
    return 'none';
  }

  const { from, to } = state;
  if (!from) return 'none';
  if (from && to && from === to && day === from) return 'start';
  if (day === from) return 'start';
  if (to && day === to) return 'end';
  if (to && day > from && day < to) return 'in-range';
  return 'none';
}

/** The list of month keys (YYYY-MM) a multi-month view should render. */
export function monthsToShow(viewMonth: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addMonthsToKey(viewMonth, i));
}

// Re-export the day-math helpers the component needs so call sites import from
// one place.
export { addDays, daysBetween };
